import React, { useEffect, useState, useRef } from 'react';
import { challengesAPI, uploadAPI, plansAPI } from '../../lib/api';
import Layout from '../../components/Layout';
import HLSPlayer from '../../components/HLSPlayer';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, X, Trash2, Upload, Loader2, Video, Edit, Trophy, Users } from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

export default function CoachChallenges() {
  const navigate = useNavigate();
  const [challenges, setChallenges] = useState([]);
  const [weeks, setWeeks] = useState([1, 2, 3, 4]); // sane default until plans load
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ week_number: 1, title: '', description: '', prompt: '', video_filename: '' });
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => { load(); }, []);

  const load = async () => {
    try {
      const [chRes, plRes] = await Promise.all([
        challengesAPI.list(),
        plansAPI.list().catch(() => ({ data: [] })),
      ]);
      const cs = chRes.data || [];
      setChallenges(cs);
      // Dynamic week list: every published plan week + every existing challenge week + 4 buffer ahead
      const planWeeks = (plRes.data || []).map(p => p.week_number).filter(Number.isFinite);
      const challengeWeeks = cs.map(c => c.week_number).filter(Number.isFinite);
      const baseMax = Math.max(1, ...planWeeks, ...challengeWeeks);
      const set = new Set();
      for (let w = 1; w <= baseMax + 4; w++) set.add(w);
      planWeeks.forEach(w => set.add(w));
      challengeWeeks.forEach(w => set.add(w));
      setWeeks([...set].sort((a, b) => a - b));
    } catch { /* ignore */ }
    setLoading(false);
  };

  const resetForm = () => {
    setForm({ week_number: 1, title: '', description: '', prompt: '', video_filename: '' });
    setEditingId(null);
    setShowForm(false);
  };

  const handleEdit = (c) => {
    setForm({
      week_number: c.week_number,
      title: c.title,
      description: c.description || '',
      prompt: c.prompt || '',
      video_filename: c.video_filename || '',
    });
    setEditingId(c.id);
    setShowForm(true);
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setUploadProgress(0);
    try {
      const res = await uploadAPI.video(file, (pe) => {
        setUploadProgress(Math.round((pe.loaded * 100) / pe.total));
      });
      setForm(f => ({ ...f, video_filename: res.data.filename }));
      toast.success('Video uploaded');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    }
    setUploading(false);
    setUploadProgress(0);
    if (fileRef.current) fileRef.current.value = '';
  };

  const handleSave = async () => {
    if (!form.title.trim()) { toast.error('Title required'); return; }
    setSaving(true);
    try {
      if (editingId) {
        await challengesAPI.update(editingId, form);
        toast.success('Challenge updated');
      } else {
        await challengesAPI.create(form);
        toast.success('Challenge created');
      }
      resetForm();
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    }
    setSaving(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this challenge? All player submissions will also be removed.')) return;
    try {
      await challengesAPI.delete(id);
      toast.success('Deleted');
      load();
    } catch { toast.error('Delete failed'); }
  };

  return (
    <Layout title="WEEKLY CHALLENGES" showBack>
      <div className="px-4 py-6 space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="font-['Barlow_Condensed'] text-xl font-bold tracking-tight">CHALLENGES</h2>
          <div className="flex gap-2">
            <button
              onClick={() => navigate('/coach/challenge-submissions')}
              className="bg-white border border-[#E2E8F0] hover:border-[#2563EB] text-[#2563EB] font-bold uppercase tracking-wide rounded-lg px-3 py-2 text-xs flex items-center gap-1"
              data-testid="view-submissions-btn"
            >
              <Users size={14} /> Submissions
            </button>
            <button
              onClick={() => { resetForm(); setShowForm(true); }}
              className="bg-[#FF9100] hover:bg-[#FFA733] text-white font-bold uppercase tracking-wide rounded-lg px-3 py-2 text-xs flex items-center gap-1"
              data-testid="add-challenge-btn"
            >
              <Plus size={14} /> Add
            </button>
          </div>
        </div>

        <AnimatePresence>
          {showForm && (
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 20 }} className="bg-white border border-[#E2E8F0] rounded-xl p-5 space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="font-['Barlow_Condensed'] text-base font-bold">{editingId ? 'EDIT CHALLENGE' : 'NEW CHALLENGE'}</h3>
                <button onClick={resetForm} className="text-[#64748B]"><X size={18} /></button>
              </div>

              <div>
                <label className="text-[10px] uppercase tracking-widest text-[#64748B] font-bold mb-1 block">Week</label>
                <select
                  value={form.week_number}
                  onChange={e => setForm(f => ({ ...f, week_number: parseInt(e.target.value) }))}
                  disabled={!!editingId}
                  className="w-full bg-[#F8FAFC] border border-[#E2E8F0] rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#FF9100] disabled:opacity-50"
                  data-testid="challenge-week-select"
                >
                  {weeks.map(w => <option key={w} value={w}>Week {w}</option>)}
                </select>
              </div>

              <input
                value={form.title}
                onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
                placeholder="Challenge title (e.g. Backyard Stickhandling Showcase)"
                className="w-full bg-[#F8FAFC] border border-[#E2E8F0] rounded-lg px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#FF9100]"
                data-testid="challenge-title-input"
              />

              <textarea
                value={form.description}
                onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                placeholder="Short description of the challenge"
                className="w-full bg-[#F8FAFC] border border-[#E2E8F0] rounded-lg px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#FF9100] resize-none h-20"
                data-testid="challenge-desc-input"
              />

              <textarea
                value={form.prompt}
                onChange={e => setForm(f => ({ ...f, prompt: e.target.value }))}
                placeholder="The mission prompt — what should the player film and submit?"
                className="w-full bg-[#F8FAFC] border border-[#E2E8F0] rounded-lg px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#FF9100] resize-none h-20"
                data-testid="challenge-prompt-input"
              />

              <div>
                <label className="text-[10px] uppercase tracking-widest text-[#64748B] font-bold mb-1 block">Coach Video</label>
                <button
                  onClick={() => fileRef.current?.click()}
                  disabled={uploading}
                  className="w-full border-2 border-dashed border-[#E2E8F0] hover:border-[#FF9100] rounded-lg p-3 text-sm flex items-center justify-center gap-2 transition-colors"
                  data-testid="challenge-upload-btn"
                >
                  {uploading ? <><Loader2 size={14} className="animate-spin" /> Uploading {uploadProgress}%</> :
                   form.video_filename ? <><Video size={14} className="text-[#10B981]" /> Video attached — tap to replace</> :
                   <><Upload size={14} className="text-[#64748B]" /> Upload video</>}
                </button>
                {uploading && (
                  <div className="mt-2 w-full bg-[#E2E8F0] rounded-full h-1.5">
                    <div className="bg-[#FF9100] h-1.5 rounded-full transition-all" style={{ width: `${uploadProgress}%` }} />
                  </div>
                )}
                <input ref={fileRef} type="file" accept="video/*" onChange={handleUpload} className="hidden" />
              </div>

              <button
                onClick={handleSave}
                disabled={saving}
                className="w-full bg-[#FF9100] hover:bg-[#FFA733] text-white font-bold uppercase tracking-wide rounded-lg px-8 py-4 transition-colors disabled:opacity-50"
                data-testid="challenge-save-btn"
              >
                {saving ? 'Saving...' : editingId ? 'Update Challenge' : 'Create Challenge'}
              </button>
            </motion.div>
          )}
        </AnimatePresence>

        {loading ? (
          <div className="flex justify-center py-12"><div className="spinner" /></div>
        ) : challenges.length === 0 ? (
          <div className="bg-white border border-[#E2E8F0] rounded-xl p-8 text-center">
            <Trophy size={32} className="mx-auto text-[#CBD5E1] mb-2" />
            <p className="text-[#64748B] text-sm">No challenges yet. Tap Add to create one.</p>
          </div>
        ) : (
          <div className="space-y-2">
            {challenges.map((c) => (
              <div key={c.id} className="bg-white border border-[#E2E8F0] rounded-xl p-4 flex items-center gap-3" data-testid={`challenge-item-${c.id}`}>
                <div className="w-11 h-11 rounded-lg bg-[#FF9100]/10 border border-[#FF9100]/30 flex items-center justify-center flex-shrink-0">
                  <Trophy size={18} className="text-[#FF9100]" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-[10px] uppercase tracking-widest text-[#FF9100] font-bold">Week {c.week_number}</p>
                  <h4 className="font-['Barlow_Condensed'] text-sm font-bold truncate">{c.title}</h4>
                  <div className="flex items-center gap-2 mt-1">
                    {c.video_filename && <Video size={10} className="text-[#2563EB]" />}
                    <span className="text-[9px] text-[#64748B]">{c.submission_count || 0} {c.submission_count === 1 ? 'submission' : 'submissions'}</span>
                  </div>
                </div>
                <button onClick={() => handleEdit(c)} className="p-2 text-[#64748B] hover:text-[#2563EB]" data-testid={`edit-challenge-${c.id}`}>
                  <Edit size={16} />
                </button>
                <button onClick={() => handleDelete(c.id)} className="p-2 text-[#64748B] hover:text-red-400" data-testid={`delete-challenge-${c.id}`}>
                  <Trash2 size={16} />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
}
